/* ==========================================================================
   blv-header.js — scroll state, mobile drawer, announcement rotator
   ========================================================================== */

(function () {
  'use strict';

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

  /* Each initialised group keeps its teardown functions here so unload can undo
     exactly what load did — no duplicate listeners across re-renders. */
  var instances = new WeakMap();

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* ---------- Scroll state: transparent header turns solid ---------- */
  function initScrollState(group, teardown) {
    if (!group.classList.contains('blv-header-group--overlay')) return;

    var offset = parseInt(group.dataset.scrollOffset, 10);
    if (isNaN(offset)) offset = 0;

    var onScroll = function () {
      group.classList.toggle('is-scrolled', window.scrollY > offset);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    teardown.push(function () {
      window.removeEventListener('scroll', onScroll);
    });
  }

  /* ---------- Mobile drawer ---------- */
  function initDrawer(group, teardown) {
    var drawer = group.querySelector('[data-blv-drawer]');
    var overlay = group.querySelector('[data-blv-drawer-overlay]');
    var openBtn = group.querySelector('[data-blv-drawer-open]');
    var closeBtn = group.querySelector('[data-blv-drawer-close]');

    if (!drawer || !openBtn) return;

    var lastFocused = null;

    var open = function () {
      lastFocused = document.activeElement;
      drawer.classList.add('is-open');
      drawer.removeAttribute('aria-hidden');
      if (overlay) overlay.classList.add('is-open');
      openBtn.setAttribute('aria-expanded', 'true');
      document.documentElement.style.overflow = 'hidden';

      var first = drawer.querySelector(FOCUSABLE);
      if (first) first.focus();
    };

    var close = function () {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      if (overlay) overlay.classList.remove('is-open');
      openBtn.setAttribute('aria-expanded', 'false');
      document.documentElement.style.overflow = '';

      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    };

    /* Focus trap — Tab cycles inside the drawer while it is open. */
    var onKeydown = function (event) {
      if (!drawer.classList.contains('is-open')) return;

      if (event.key === 'Escape') {
        close();
        return;
      }

      if (event.key !== 'Tab') return;

      var items = Array.prototype.filter.call(
        drawer.querySelectorAll(FOCUSABLE),
        function (el) {
          return el.offsetParent !== null;
        }
      );
      if (!items.length) return;

      var first = items[0];
      var last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    openBtn.addEventListener('click', open);
    if (closeBtn) closeBtn.addEventListener('click', close);
    if (overlay) overlay.addEventListener('click', close);
    document.addEventListener('keydown', onKeydown);

    /* Closing on navigation keeps the drawer from persisting across a soft
       route change; harmless on a full page load. */
    var links = drawer.querySelectorAll('a[href]');
    Array.prototype.forEach.call(links, function (link) {
      link.addEventListener('click', close);
    });

    teardown.push(function () {
      document.removeEventListener('keydown', onKeydown);
      document.documentElement.style.overflow = '';
    });
  }

  /* ---------- Announcement bar: rotator ---------- */
  function initAnnouncement(group, teardown) {
    var bar = group.querySelector('[data-blv-announcement]');
    if (!bar) return;

    var items = bar.querySelectorAll('[data-blv-announcement-item]');
    var speed = parseInt(bar.dataset.rotateSpeed, 10);

    if (items.length < 2 || !speed || prefersReducedMotion()) return;

    var index = 0;
    var timer = window.setInterval(function () {
      items[index].classList.remove('is-active');
      index = (index + 1) % items.length;
      items[index].classList.add('is-active');
    }, speed);

    teardown.push(function () {
      window.clearInterval(timer);
    });
  }

  /* ---------- Cart icon: open the drawer instead of navigating ---------- */
  function initCartTrigger(group, teardown) {
    var trigger = group.querySelector('[data-blv-cart-open]');
    if (!trigger) return;

    var onClick = function (event) {
      /* Let modified clicks do what the shopper asked: new tab, new window,
         download. Only a plain primary click should be captured. */
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

      var drawer = document.querySelector('cart-drawer');
      if (!drawer || typeof drawer.open !== 'function') return;

      event.preventDefault();
      drawer.open(trigger);
    };

    var onKeydown = function (event) {
      if (event.code !== 'Space' && event.key !== ' ') return;

      var drawer = document.querySelector('cart-drawer');
      if (!drawer || typeof drawer.open !== 'function') return;

      event.preventDefault();
      drawer.open(trigger);
    };

    trigger.addEventListener('click', onClick);
    trigger.addEventListener('keydown', onKeydown);

    teardown.push(function () {
      trigger.removeEventListener('click', onClick);
      trigger.removeEventListener('keydown', onKeydown);
    });
  }

  /* ---------- Search modal: close it on section unload ---------- */
  function initSearchModal(group, teardown) {
    var modal = group.querySelector('details-modal');
    if (!modal) return;

    teardown.push(function () {
      if (typeof modal.close === 'function' && typeof modal.isOpen === 'function' && modal.isOpen()) {
        modal.close(false);
      }
    });
  }

  /* ---------- Cart count ---------- */
  function initCartCount(group, teardown) {
    var host = document.getElementById('cart-icon-bubble');
    var badge = group.querySelector('[data-blv-cart-count]');

    if (!host || !badge) return;

    var render = function () {
      var bubble = host.querySelector('.cart-count-bubble');

      /* No bubble in the re-rendered markup means an empty cart. */
      if (!bubble) {
        badge.hidden = true;
        return;
      }

      /* Spotlight omits the numeral above 99 items; keep the last known text in
         that case rather than blanking the badge. */
      var value = bubble.querySelector('[aria-hidden="true"]');
      if (value) badge.textContent = value.textContent.trim();
      badge.hidden = false;
    };

    var observer = new MutationObserver(render);
    observer.observe(host, { childList: true, subtree: true });

    teardown.push(function () {
      observer.disconnect();
    });
  }

  /* ---------- Wiring ---------- */
  function init(root) {
    var scope = root || document;
    var groups = scope.querySelectorAll('[data-blv-header-group]');

    Array.prototype.forEach.call(groups, function (group) {
      if (instances.has(group)) return;

      var teardown = [];
      instances.set(group, teardown);

      initScrollState(group, teardown);
      initDrawer(group, teardown);
      initAnnouncement(group, teardown);
      initCartTrigger(group, teardown);
      initSearchModal(group, teardown);
      initCartCount(group, teardown);
    });
  }

  function destroy(root) {
    var scope = root || document;
    var groups = scope.querySelectorAll('[data-blv-header-group]');

    Array.prototype.forEach.call(groups, function (group) {
      var teardown = instances.get(group);
      if (!teardown) return;

      teardown.forEach(function (fn) {
        fn();
      });
      instances.delete(group);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    destroy(event.target);
  });
})();
